import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { SortDirection } from '../../../core/enums/sort-direction';
import * as baseState from '../../../core/models/base-state/base-state';
import { setGlobalPageSize } from '../../../core/store/actions/app.actions';
import { PatientResult } from '../../models/patient-result';
import * as labResultActions from '../actions/patient-result.actions';
import { PatientResultsFilters } from '../actions/patient-result.actions';

export interface PatientResultsState extends EntityState<PatientResult> {
  baseState: baseState.BaseState;
  pageSize: number; //decouple page size from base state to prevent overrides
  filters: PatientResultsFilters;
  // Scroll/selection memory for returning from a result detail (see rememberListPosition)
  lastViewedResultId: number | null;
  listScrollTop: number;
  /**
   * Failures from the single-result detail read, kept apart from `baseState.error`.
   *
   * They shared one slot until PBI 54129: a failed detail read wrote the slot the list's error state
   * reads, so after being bounced to /error and pressing "Try again" (which is location.back()) the
   * patient landed on a perfectly healthy list showing "Could not load your results". Mirrors the
   * `detailError` split health-concerns already uses.
   */
  detailError: unknown;
}

export const patientResultsAdapter = createEntityAdapter<PatientResult>({
  selectId: patientResult => patientResult.patientLabResultId,
});

export const initialState: PatientResultsState = patientResultsAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  pageSize: baseState.DEFAULT_PAGE_SIZE,
  filters: {
    searchTerm: null,
    abnormalTypeId: null,
    labResultStatusTypeId: null,
    sortDirection: SortDirection.Descending,
    sortColumn: 'observationDateUtc',
  },
  lastViewedResultId: null,
  listScrollTop: 0,
  detailError: null,
});

export const patientResultsFeatureKey = 'patient-lab-results';

export const patientResultsReducer = createReducer(
  initialState,
  on(labResultActions.getPatientResults, (state, { filters }): PatientResultsState => {
    return {
      ...state,
      filters,
      // A fresh query invalidates any remembered list position.
      lastViewedResultId: null,
      listScrollTop: 0,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
    };
  }),

  on(
    labResultActions.getPatientResultsSuccess,
    (state, { patientResults }): PatientResultsState =>
      patientResultsAdapter.addMany(patientResults, {
        ...state,
      })
  ),

  on(
    labResultActions.getPatientResultsSuccess,
    (state, { pageNumber, isAtEndOfData, totalRecordCount }): PatientResultsState => {
      return {
        ...state,
        baseState: {
          ...state.baseState,
          isLoading: false,
          pageNumber,
          isAtEndOfData,
          totalRecordCount,
          pageSize: baseState.DEFAULT_PAGE_SIZE,
        },
      };
    }
  ),

  on(labResultActions.getPatientResultsFailure, (state, { error }): PatientResultsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
    };
  }),

  on(labResultActions.getPatientResults, (state): PatientResultsState => patientResultsAdapter.removeAll(state)),

  // Retry of a failed page: clear the error and show loading, but keep pageNumber and every loaded
  // entity. Deliberately no removeAll — that is the difference from getPatientResults above.
  on(
    labResultActions.retryPage,
    (state): PatientResultsState => ({
      ...state,
      baseState: { ...state.baseState, error: null, isLoading: true },
    })
  ),

  on(labResultActions.nextPage, (state, { pageNumber }): PatientResultsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber,
      },
    };
  }),

  on(labResultActions.nextPage, (state): PatientResultsState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
    };
  }),

  on(labResultActions.getPatientResultsById, (state): PatientResultsState => {
    return {
      ...state,
      detailError: null,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
    };
  }),

  on(labResultActions.getPatientResultsByIdSuccess, (state, { patientResult }): PatientResultsState => {
    return patientResultsAdapter.upsertOne(patientResult, {
      ...state,
      detailError: null,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    });
  }),

  // Writes detailError, NOT baseState.error — see the field's docstring.
  on(labResultActions.getPatientResultsByIdFailure, (state, { error }): PatientResultsState => {
    return {
      ...state,
      detailError: error,
      baseState: {
        ...state.baseState,
        isLoading: false,
      },
    };
  }),

  on(setGlobalPageSize, (state, { pageSize }): PatientResultsState => {
    return {
      ...state,
      pageSize,
    };
  }),

  on(
    labResultActions.rememberListPosition,
    (state, { lastViewedResultId, scrollTop }): PatientResultsState => ({
      ...state,
      lastViewedResultId,
      listScrollTop: scrollTop,
    })
  ),

  on(
    labResultActions.clearListPosition,
    (state): PatientResultsState => ({
      ...state,
      lastViewedResultId: null,
      listScrollTop: 0,
    })
  )
);
